/**
 * Trip duplicate detection for Device Sync — fingerprint by dates / cruise metadata.
 * Sync identity remains tripId; these helpers only prevent accidental second creates.
 */

import type { Trip } from '../types/client';
import { dateRangesOverlap } from '../services/cruiseItineraryMapper';

export function normalizeTripTitle(title: string | null | undefined): string {
  return (title ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export type TripFingerprintInput = {
  title?: string | null;
  tripType?: Trip['tripType'];
  startDate?: string | null;
  endDate?: string | null;
  sourceItineraryId?: string | null;
};

/** Candidate vs existing: date-overlap match used for import warnings. */
export function tripsOverlapByDates(
  a: TripFingerprintInput,
  b: TripFingerprintInput
): boolean {
  return dateRangesOverlap(
    a.startDate ?? null,
    a.endDate ?? null,
    b.startDate ?? null,
    b.endDate ?? null
  );
}

/**
 * Stronger “same cruise / same trip” fingerprint for dedupe.
 * Matches when dates overlap and any of:
 * - same sourceItineraryId (when both set)
 * - same startDate + normalized title
 * - both cruise (or candidate cruise) with same startDate
 */
export function tripsFingerprintMatch(
  a: TripFingerprintInput,
  b: TripFingerprintInput
): boolean {
  if (!tripsOverlapByDates(a, b)) return false;

  const aItin = (a.sourceItineraryId ?? '').trim();
  const bItin = (b.sourceItineraryId ?? '').trim();
  if (aItin && bItin && aItin === bItin) return true;

  const aStart = a.startDate ?? null;
  const bStart = b.startDate ?? null;
  if (aStart && bStart && aStart === bStart) {
    const aTitle = normalizeTripTitle(a.title);
    const bTitle = normalizeTripTitle(b.title);
    if (aTitle && bTitle && aTitle === bTitle) return true;

    const aCruise = a.tripType === 'cruise' || a.tripType == null;
    const bCruise = b.tripType === 'cruise' || b.tripType == null;
    if (a.tripType === 'cruise' && b.tripType === 'cruise') return true;
    if (a.tripType === 'cruise' && bCruise && aTitle && bTitle && aTitle === bTitle) {
      return true;
    }
    if (b.tripType === 'cruise' && aCruise && aTitle && bTitle && aTitle === bTitle) {
      return true;
    }
  }

  return false;
}

export function findFingerprintMatches(
  existing: Trip[],
  candidate: TripFingerprintInput
): Trip[] {
  return existing.filter((t) =>
    tripsFingerprintMatch(candidate, {
      title: t.title,
      tripType: t.tripType,
      startDate: t.startDate,
      endDate: t.endDate,
      sourceItineraryId: t.sourceItineraryId,
    })
  );
}

/** Prefer trip with coords + tasks (healthier Device Sync / iOS copy). */
export function preferKeeperTrip(a: Trip, b: Trip): Trip {
  const score = (t: Trip): number => {
    const coords = Object.keys(t.cityCoords ?? {}).length;
    const tasks = t.taskItems?.length ?? 0;
    const days = t.itineraryDays?.length ?? 0;
    const cities = t.cityIds?.length ?? 0;
    return coords * 10 + tasks * 3 + days + cities;
  };
  return score(a) >= score(b) ? a : b;
}

/**
 * Among fingerprint-duplicate groups, return ids that should not be pushed
 * (losers vs the preferred keeper in each group).
 */
export function orphanTripIdsToSkipPush(trips: Trip[]): Set<string> {
  const skip = new Set<string>();
  const remaining = [...trips];
  while (remaining.length > 0) {
    const head = remaining.shift()!;
    if (skip.has(head.id)) continue;
    const mates: Trip[] = [head];
    for (let i = remaining.length - 1; i >= 0; i -= 1) {
      const other = remaining[i];
      if (skip.has(other.id)) continue;
      if (
        tripsFingerprintMatch(
          {
            title: head.title,
            tripType: head.tripType,
            startDate: head.startDate,
            endDate: head.endDate,
            sourceItineraryId: head.sourceItineraryId,
          },
          {
            title: other.title,
            tripType: other.tripType,
            startDate: other.startDate,
            endDate: other.endDate,
            sourceItineraryId: other.sourceItineraryId,
          }
        )
      ) {
        mates.push(other);
        remaining.splice(i, 1);
      }
    }
    if (mates.length < 2) continue;
    let keeper = mates[0];
    for (let i = 1; i < mates.length; i += 1) {
      keeper = preferKeeperTrip(keeper, mates[i]);
    }
    for (const m of mates) {
      if (m.id !== keeper.id) skip.add(m.id);
    }
  }
  return skip;
}
